import { Button, Popover, Space, Table } from 'antd';
import { constants, Investor } from 'procura-shared';
import React, { useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';
import { format, round } from '../utilities/Utilities';
import { DetailsAddButton, DetailsTable, Editable } from './Components';

type SharesTableProps = {
  shares: Object;
  roles: Object;
  investors: Investor[];
  loading?: boolean;
  adminMode?: boolean;
  onSelect?: (investor: Investor) => void;
  onChange?: (shareEmail: string, newValue: number) => void;
  onRoleChange?: (shareEmail: string, newRole: string | null) => void;
};

export const SharesTable = ({
  shares = {},
  roles = {},
  investors = [],
  loading = false,
  adminMode = false,
  onSelect,
  onChange,
  onRoleChange,
}: SharesTableProps) => {
  const { t } = useTranslation();
  const [selectedInvestor, setSelectedInvestor] = useState<Investor>(undefined);

  const noData = (): Boolean => shares == null || Object.keys(shares).length === 0;
  const getRelativeShare = (share: number): number => (share / totalShares()) * 100;
  const getInvestor = (investorId: string) => investors.find((investor) => investor.id === investorId);
  const isBigTable = (): Boolean => (noData() ? false : Object.keys(shares).length > 8);
  const totalShares = (): number => (Object.values(shares) ?? []).reduce((val, acc) => (acc += val), 0);
  // const previousRelativeProgress = (index: number): number =>
  //   getRelativeShare(
  //     Object.values(shares)
  //       .slice(0, index)
  //       .reduce((val, acc) => (acc += val), 0)
  //   );
  const tableData = (): Array<Record<string, any>> =>
    Object.keys(shares ?? {}).map((investorId) => ({
      key: investorId,
      email: getInvestor(investorId)?.name ?? getInvestor(investorId)?.contactEmail ?? investorId ?? '-',
      shares: shares[investorId],
      role: roles?.[investorId],
      relativeShares: getRelativeShare(shares[investorId]),
    }));

  const shareClickHandler = (investorId: string) => {
    setSelectedInvestor(getInvestor(investorId));
  };

  const shareSubmitHandler = (shareId: string, newValue: number) => {
    onChange?.(shareId, newValue);
  };

  const moreInfoClickHandler = () => {
    onSelect?.(selectedInvestor);
  };

  const tableColumns = () => [
    {
      title: () => (
        <Space className="pg-justify-space-between">
          {t('investor')}
          {adminMode && (
            <DetailsAddButton
              label={t('addInvestor')}
              placeholder={t('email')}
              onConfirm={(value) => shareSubmitHandler(value, 10)}
            />
          )}
        </Space>
      ),
      dataIndex: 'email',
      key: 'email',
      render: (text, item) => (
        <Popover
          placement="right"
          content={
            <Space direction="vertical" align="center">
              <DetailsTable loading={false} details={selectedInvestor} keys={constants.INVESTOR_KEYS} />
              {adminMode && (
                <Button type="primary" onClick={moreInfoClickHandler}>
                  {t('moreInfo')}
                </Button>
              )}
            </Space>
          }
          trigger="click">
          <a href="_blank" onClick={() => shareClickHandler(item.key)}>
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[calls.auth.getCurrentEmail()]}
              textToHighlight={text ? text.toString() : ''}
            />
          </a>
        </Popover>
      ),
    },
    {
      title: t('role'),
      dataIndex: 'role',
      key: 'role',
      render: (text, item) => (
        <>
          {adminMode ? (
            <Editable
              value={text}
              render={<>{!item.role ? '' : t(`roles.${item.role}`)}</>}
              editMode={adminMode}
              detailKey="roles"
              options={[
                { value: 'CHAIRMAN', label: t('roles.CHAIRMAN') },
                { value: 'DEPUTY', label: t('roles.DEPUTY') },
                { value: 'MEMBER', label: t('roles.MEMBER') },
                { value: 'DIRECTOR', label: t('roles.DIRECTOR') },
              ]}
              onSubmit={(newRole: string) => onRoleChange?.(item.key, newRole)}
              onDelete={() => onRoleChange?.(item.key, null)}
            />
          ) : (
            <>{!item.role ? '' : t(`roles.${item.role}`)}</>
          )}
        </>
      ),
    },
    {
      title: t('shares'),
      dataIndex: 'shares',
      key: 'shares',
      render: (text, item) => (
        <Editable
          value={text}
          render={<>{format(text)}</>}
          editMode={adminMode}
          onSubmit={(newValue: number) => shareSubmitHandler(item.key, newValue)}
          onDelete={() => shareSubmitHandler(item.key, 0)}
        />
      ),
    },
    {
      title: t('relativeShares'),
      dataIndex: 'relativeShares',
      key: 'relativeShares',
      render: (text, _item, index) => `${round(text, 2)}%`,
      // Removed progress due to request from Claus in May 2021
      // <Progress
      //   key={index}
      //   size="small"
      //   status="normal"
      //   percent={previousRelativeProgress(index) + text}
      //   success={{ percent: previousRelativeProgress(index) }}
      //   format={() => `${round(text, 1)}%`}
      // />
    },
  ];

  return (
    <Table
      pagination={false}
      loading={loading}
      bordered
      dataSource={tableData()}
      columns={tableColumns()}
      scroll={{ y: 500 }}
      sticky
      size={isBigTable() ? 'small' : 'large'}
      summary={(pageData) => {
        return (
          <>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}></Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              <Table.Summary.Cell index={2}> {format(round(totalShares(), 2))} </Table.Summary.Cell>
              <Table.Summary.Cell index={3}>100%</Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        );
      }}
    />
  );
};
